import { CellContext } from '@tanstack/react-table';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { vars } from 'src/core/themes';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { FormatOption } from 'src/modals/EditTableColumns';
import { getColorForValue } from 'src/utils/cellUtils';
import { precisionOptionMappings } from 'src/utils/numberFormatter';
import { SectionType } from 'src/utils/types/sectionType';

import { BaseCell, BaseCellProps } from './BaseCell';
import { ColorThreshold } from './ConditionalCell/ConditionalCell.types';
import * as styles from './MoneyCell.css';

export interface MoneyCellProps<TData = unknown>
  extends Omit<BaseCellProps, 'children'> {
  context?: CellContext<TData, number | null | undefined>;
  value?: number;
  currencyCode?: string | null;
  decimalPlaces?: number;
  precision: FormatOption;
  columnId: string; // For useColumnUserSetting hook
  sectionType?: SectionType; // For useColumnUserSetting hook
  showNegativeInRed?: boolean;
  compact?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onValueChange?: (value: number | null) => void;
  thresholds?: ColorThreshold[];
}

export function MoneyCell<TData>({
  context,
  currencyCode = 'USD',
  decimalPlaces = 2,
  precision,
  columnId,
  sectionType = SectionType.Sales,
  showNegativeInRed = false,
  compact = false,
  editable = false,
  disabled = false,
  onValueChange,
  thresholds,
  ...props
}: MoneyCellProps<TData>) {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = getUiCurrency(
    currencyCode ?? loginContext?.user?.activeAccount.currencyCode
  );

  const effectivePrecision = precision;

  const value = props.value ?? context?.getValue();
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize edit value when entering edit mode
  useEffect(() => {
    if (value != null) {
      setEditValue(value.toString());
    }
  }, [value]);

  const handleClick = () => {
    if (editable && !disabled) {
      setEditValue(value?.toString() || '');
    }
  };

  const handleSave = () => {
    const numValue = editValue === '' ? null : parseFloat(editValue);
    if (!isNaN(numValue || 0) && onValueChange) {
      onValueChange(numValue);
    }
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '');
  };

  // Edit mode
  if (editable) {
    return (
      <BaseCell
        {...props}
        className={clsx(
          disabled ? styles.moneyCellDisabled : styles.moneyCellEditable,
          props.className
        )}
      >
        <div className={styles.container}>
          <div>{currencyCode}</div>
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            className={clsx(styles.input, disabled && styles.inputDisabled)}
          />
        </div>
      </BaseCell>
    );
  }

  // Display mode
  if (!value) {
    return <BaseCell />;
  }

  const isNegative = value < 0;
  let absoluteValue = Math.abs(value);

  // Get conditional colors if thresholds are provided
  const conditionalColors =
    thresholds && thresholds.length > 0 && value != null
      ? getColorForValue(value, thresholds)
      : null;

  // Apply precision formatting (thousands, millions, etc.)
  const {
    suffix = '',
    value: precisionDivisor = 1,
    roundToDigits = decimalPlaces,
  } = precisionOptionMappings[effectivePrecision] ?? {};

  // Divide by precision value (e.g., 1000 for thousands, 1000000 for millions)
  absoluteValue = absoluteValue / precisionDivisor;

  // Use roundToDigits from precision if available, otherwise use decimalPlaces
  const effectiveDecimalPlaces = roundToDigits ?? decimalPlaces;

  // Format the number
  let formattedValue: string;
  if (compact && absoluteValue >= 1000) {
    // Compact notation for large numbers
    const formatter = new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    });
    formattedValue = formatter.format(absoluteValue);
  } else {
    // Standard formatting with commas
    formattedValue = absoluteValue.toLocaleString('en-US', {
      minimumFractionDigits: effectiveDecimalPlaces,
      maximumFractionDigits: effectiveDecimalPlaces,
    });
  }

  // Add suffix (e.g., 'k' for thousands, 'M' for millions)
  if (suffix) {
    formattedValue = formattedValue + suffix;
  }

  return (
    <BaseCell
      {...props}
      showPadding={props.showPadding ?? !thresholds}
      isFullWidth={props.isFullWidth ?? Boolean(thresholds)}
    >
      <div
        className={clsx(
          styles.container,
          editable && !disabled && styles.editableContainer,
          disabled && styles.disabledContainer
        )}
        onClick={handleClick}
        style={
          conditionalColors
            ? {
                backgroundColor: conditionalColors.backgroundColor,
                color: conditionalColors.color,
                width: '100%',
                height: '100%',
                padding: '4px',
                borderRadius: vars.borderRadius.s,
              }
            : undefined
        }
      >
        <span>{uiCurrency?.sym || currencyCode}</span>
        <span className={styles.value}>
          {isNegative && '−'}
          {formattedValue}
        </span>
      </div>
    </BaseCell>
  );
}
